import React, { useState, useContext, useRef, useMemo } from 'react';
import { func } from 'prop-types';
import { Box, Flex } from 'rebass';
import { connect } from 'react-redux';
import { ButtonStyled, TextStyled, useTokens } from '@nike/nike-design-system-components';
import { CSVLink } from 'react-csv';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';

import TableServerSide from '../../../components/tableServerSide/TableServerSide';
import { ActionItemList } from '../../common/ActionItemList';
import IconMenu from '../../../components/IconMenu';
import UploadCatalogModal from '../../catalogs/uploadCatalog/modals/UploadCatalogModal';
import DateChange from '../catalogsDateChange/DateChange';
import { showToast } from '../../../components/toast/actions';

import { generateValidationReport, fetchAllCatalogs, deleteCatalog, deleteAllCatalogs } from '../../catalogs/actions';

import { getCatalogListColumns } from './columns';

import { formatCatalogForCSV } from '../../util';

import { TranslatedCopy } from '../../../intl';

import DownloadProducts from '../downloadCatalogProducts/DownloadProducts';

import useModal from '../../../hooks/useModal';
import IndexCatalogModal from '../../catalogs/catalogsCreateOrEdit/modals/IndexCatalogModal';
import UnIndexCatalogModal from '../../catalogs/catalogsCreateOrEdit/modals/UnIndexCatalogModal';
import { MAX_CATALOG_INDEX } from '../constants';
import { GA_EVENT_ADMIN_MASS_CREATE_CATALOG_CLICK, sendEventToGA } from '../../../analytics/constants';

const CatalogsList = ({ generateValidationReport, showToast }) => {
  const [isUploadCatalogModalOpen, openUploadCatalogModal, closeUploadCatalogModal] = useModal();
  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();
  const [isDateChangeModalOpen, openDateChangeModal, closeDateChangeModal] = useModal();
  const [isDownloadProductsModalOpen, openDownloadProductsModal, closeDownloadProductsModal] = useModal();
  const [isIndexerModalOpen, setIsIndexerModalOpen] = useState(false);
  const [indexerModalType, setIndexerModalType] = useState(null);
  const [catalogIdsForIndexer, setCatalogIdsForIndexer] = useState([]);
  const [catalogIdsNamesIndexer, setCatalogNamesForIndexer] = useState([]);
  const [allCatalogs, setAllCatalogs] = useState([]);

  const csvLinkRef = useRef(null);

  const tokens = useTokens();

  const {
    tableInstance: { selectedRowIds = {}, isAllRowsSelected, selectedFlatRows },
    handleClearSortAndFilters,
    totalResources,
    sortQueryParams,
    filterQueryParams,
    refreshTableData
  } = useContext(TableServerSideContext);

  const selectedCatalogIds = selectedFlatRows?.reduce((acc, row) => ({ ...acc, [row?.original?.id]: true }), {}) || {};
  const selectedCatalogIdsArr = selectedFlatRows?.map(item => item.original.id);
  const selectedCatalogIdsCombined = selectedCatalogIdsArr?.join(',');

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const handleFetchAllCatalogs = async idsOnly => {
    const fetchedCatalogs = await fetchAllCatalogs({ totalResources, sortQueryParams, filterQueryParams });

    if (idsOnly) {
      return fetchedCatalogs.map(catalog => catalog.id);
    }

    return setAllCatalogs(fetchedCatalogs);
  };

  const handleOpenIndexerModal = async type => {
    const selectedCount = selectedFlatRows.length;
    const selectedCatalogs = selectedFlatRows.map(row => row.original);

    setIndexerModalType(type);
    setIsIndexerModalOpen(true);

    if (selectedCount > MAX_CATALOG_INDEX) {
      setCatalogIdsForIndexer([]);
      setCatalogNamesForIndexer([]);
      return;
    }

    setCatalogIdsForIndexer(selectedCatalogs.map(catalog => catalog.id));
    setCatalogNamesForIndexer(selectedCatalogs.map(catalog => catalog.name));
  };

  const handleCloseIndexerModal = () => {
    setIsIndexerModalOpen(false);
    setIndexerModalType(null);
  };

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await handleFetchAllCatalogs(false);
                  toggleIsLoading(false);
                }

                clickCSVLink();

                closeActionItemModal();
                showToast({ message: 'Catalog/s downloaded', type: 'success' });
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected catalog/s?',
        title: 'Confirm Download'
      }
    },
    'Validation Report': {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Downloading Validation Report...');

                const catalogIds = isAllRowsSelected ? await handleFetchAllCatalogs(true) : selectedCatalogIds;
                await generateValidationReport(catalogIds);

                toggleIsLoading(false);

                closeActionItemModal();
              }
          }
        ],
        content: () => 'Would you like to download the Validation Report?',
        title: 'Validation Report'
      }
    },
    'Date Change': { action: () => openDateChangeModal() },
    'Download Assortment': { action: () => openDownloadProductsModal() },
    Index: { action: () => handleOpenIndexerModal('index') },
    'Un-Index': {
      action: () => handleOpenIndexerModal('unindex')
    },
    '|': { action: () => {} },
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting catalog...');

                try {
                  if (isAllRowsSelected) {
                    await deleteAllCatalogs(filterQueryParams);
                  } else {
                    const ids = Object.keys(selectedCatalogIds);

                    await Promise.all(ids.map(id => deleteCatalog(id)));
                  }

                  refreshTableData();

                  showToast(
                    {
                      type: 'success',
                      message: 'Catalogs deleted'
                    },
                    3000
                  );
                } catch (error) {
                  showToast({
                    message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
                    type: 'error',
                    error
                  });
                }

                toggleIsLoading(false);

                closeActionItemModal();

                showToast({ message: 'Catalogs deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Do you want to proceed with deleting the selected catalog/s?',
        title: 'Confirm Deletion'
      }
    }
  };

  const memoizedColumns = useMemo(() => getCatalogListColumns(), []);

  const csvLinkData = (isAllRowsSelected ? allCatalogs : selectedFlatRows?.map(row => row.original))?.map(
    ({ id, name, hasDraft, ownerId, accountIds, ...catalog }) => formatCatalogForCSV(catalog, id, name)
  );

  return (
    <Box style={{ backgroundColor: tokens.podiumCdsColorGrey50 }}>
      <Flex
        sx={{
          borderBottom: `1px solid ${tokens.podiumCdsColorGrey200}`,
          padding: '12px',
          width: '100%'
        }}
      >
        <TextStyled appearance='title2'>Catalogs</TextStyled>
      </Flex>
      <hr />

      <CSVLink data={csvLinkData || []} filename='Catalogs.csv' ref={csvLinkRef} target='_blank' />

      <Flex p='12px' width='100%' justifyContent='space-between'>
        <Flex alignItems='center'>
          <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
            {`${totalResources}`} results
          </TextStyled>

          <ButtonStyled
            appearance='text'
            background='light'
            ariaLabel='Clear All Filters'
            type='button'
            onClick={handleClearSortAndFilters}
            style={{ marginRight: '10px' }}
          >
            Clear All Filters
          </ButtonStyled>

          <ActionItemList
            selected={selectedRowIds}
            actionItems={actionItems}
            isModalOpen={isActionItemModalOpen}
            openModal={openActionItemModal}
            closeModal={closeActionItemModal}
          />
        </Flex>

        <Flex justifyContent='flex-end'>
          <IconMenu
            align='right'
            menuOptions={[
              { text: 'Create Catalog', to: '/catalogs/new' },
              {
                text: 'Mass Create Catalog',
                onClick: () => {
                  openUploadCatalogModal();
                  sendEventToGA(GA_EVENT_ADMIN_MASS_CREATE_CATALOG_CLICK);
                }
              },
              { text: 'Find & Delete Codes', to: '/catalogs/findDeleteProductCodes' }
            ]}
          />
        </Flex>
      </Flex>

      <Box px='12px'>
        <TableServerSide columns={memoizedColumns} url='v1/catalogs' includeRowSelect fullHeight />
      </Box>

      {isUploadCatalogModalOpen && (
        <UploadCatalogModal
          classname='uploadCatalog'
          closeModal={closeUploadCatalogModal}
          isOpen={isUploadCatalogModalOpen}
        />
      )}

      <DateChange
        closeDateChangeModal={closeDateChangeModal}
        isDateChangeModalOpen={isDateChangeModalOpen}
        selectedCatalogIds={selectedCatalogIds}
        refreshTableData={refreshTableData}
      />
      <DownloadProducts
        closeDownloadProductsModal={closeDownloadProductsModal}
        isDownloadProductsModalOpen={isDownloadProductsModalOpen}
        selectedFlatRows={selectedFlatRows}
        catalogId={selectedCatalogIdsCombined}
        isCatalogsList
      />
      <IndexCatalogModal
        isOpen={isIndexerModalOpen && indexerModalType === 'index'}
        closeModal={handleCloseIndexerModal}
        catalogIds={catalogIdsForIndexer}
        catalogNames={catalogIdsNamesIndexer}
        onSuccess={refreshTableData}
      />
      <UnIndexCatalogModal
        isOpen={isIndexerModalOpen && indexerModalType === 'unindex'}
        closeModal={handleCloseIndexerModal}
        catalogIds={catalogIdsForIndexer}
        catalogNames={catalogIdsNamesIndexer}
      />
    </Box>
  );
};

CatalogsList.propTypes = {
  generateValidationReport: func.isRequired,
  showToast: func.isRequired
};

export default connect(null, { generateValidationReport, showToast })(CatalogsList);
